import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { OrgReportsApi } from '@/services/org-reports.api';
import { OrgPortalApi } from '@/services/org.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Input';

/**
 * Subject-wise Teacher Count Report — org tier, read-only. Matches the
 * real `Reports\SubjectTeacherCountController` shape: per-school subject
 * list with a distinct-teacher count, plus a per-teacher subject-list
 * breakdown, built straight off `subject_teachers` (already ported)
 * rather than approximated from `timetable`.
 */
export function OrgSubjectTeacherCountPage() {
  const [schoolId, setSchoolId] = useState('');
  const [stateId, setStateId] = useState<number | ''>('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-subject-teacher-count'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: states = [] } = useQuery({
    queryKey: ['org-report-states'],
    queryFn: () => OrgReportsApi.listStates(),
  });

  const filterParams = { schoolId: schoolId || undefined, stateId: stateId === '' ? undefined : stateId };

  const { data, isFetching } = useQuery({
    queryKey: ['org-subject-teacher-count', filterParams],
    queryFn: () => OrgReportsApi.subjectTeacherCount(filterParams),
  });

  const rows = data ?? [];
  const totalTeachers = rows.reduce((sum, r) => sum + r.teachers.length, 0);
  const totalSubjectAssignments = rows.reduce((sum, r) => sum + r.subjects.reduce((s, sub) => s + sub.teacherCount, 0), 0);

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportSubjectTeacherCount(format, filterParams);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Subject-wise Teacher Count"
        description="School-wise count of teachers per subject, across your organization."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-48"
          value={stateId}
          onChange={(e) => setStateId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">All states</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      {data && (
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Teachers</div>
            <div className="text-2xl font-bold text-slate-800">{totalTeachers}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Subject Assignments</div>
            <div className="text-2xl font-bold text-slate-800">{totalSubjectAssignments}</div>
          </div>
        </div>
      )}

      {isFetching && !rows.length ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-slate-400">No schools found.</div>
      ) : (
        <div className="space-y-6">
          {rows.map((r) => (
            <div key={r.schoolId} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                <h3 className="font-semibold text-slate-800">{r.schoolName}</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Subjects</div>
                  {r.subjects.length === 0 ? (
                    <div className="text-sm text-slate-400">No subject assignments.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <tbody className="divide-y divide-slate-100">
                        {r.subjects.map((s) => (
                          <tr key={s.subjectId}>
                            <td className="py-1.5 text-slate-700">{s.subjectName}</td>
                            <td className="py-1.5 text-right font-medium text-slate-800">{s.teacherCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Teachers</div>
                  {r.teachers.length === 0 ? (
                    <div className="text-sm text-slate-400">No teachers assigned.</div>
                  ) : (
                    <ul className="space-y-1.5 text-sm">
                      {r.teachers.map((t) => (
                        <li key={t.teacherId} className="flex flex-wrap items-baseline gap-1.5">
                          <span className="font-medium text-slate-800">{t.teacherName}</span>
                          <span className="text-xs text-slate-400">— {t.subjects.join(', ')}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
